import * as React from 'react';
import { useState, useEffect, useMemo } from 'react';
import AppBar from '@mui/material/AppBar';
import Box from '@mui/material/Box';
import Toolbar from '@mui/material/Toolbar';
import IconButton from '@mui/material/IconButton';
import Typography from '@mui/material/Typography';
import Menu from '@mui/material/Menu';
import MenuIcon from '@mui/icons-material/Menu';
import Container from '@mui/material/Container';
import Button from '@mui/material/Button';
import Drawer from '@mui/material/Drawer';
import List from '@mui/material/List';
import ListItem from '@mui/material/ListItem';
import ListItemButton from '@mui/material/ListItemButton';
import ListItemText from '@mui/material/ListItemText';
import Divider from '@mui/material/Divider';
import Tooltip from '@mui/material/Tooltip';
import { useTheme } from '@mui/material/styles';
import { useNavigate, useLocation } from 'react-router-dom';
import supabase from '../../supabaseClient';
import IdConfigurationGuard from '../../Config/IdConfigurationGuard';
import AccountCircleIcon from '@mui/icons-material/AccountCircle';
import ViewSidebarIcon from '@mui/icons-material/ViewSidebar';
import ViewStreamIcon from '@mui/icons-material/ViewStream';
import ChevronLeftIcon from '@mui/icons-material/ChevronLeft';
import ChevronRightIcon from '@mui/icons-material/ChevronRight';
import LogoutIcon from '@mui/icons-material/Logout';
import VisibilityOffIcon from '@mui/icons-material/VisibilityOff';

const DRAWER_WIDTH = 280;
const RAIL_WIDTH = 72;
const BAR_HEIGHT = 64;
const BAR_HEIGHT_SM = 56;
const TRANSITION = 'width 0.25s ease, margin 0.25s ease, min-height 0.25s ease';

function NavShell({
  storageKeyPrefix,
  profileHref,
  allowedProfileTypes,
  redirectTo = '/home',
  navItems,
  children,
}) {
  const theme = useTheme();
  const navigate = useNavigate();
  const location = useLocation();
  // NavShell montuje się od nowa przy każdej nawigacji (każda strona sama owija się
  // w ManagerNavBar/WorkerNavBar/DriverNavBar), więc bez cache w localStorage menu
  // na ułamek sekundy renderowałoby się z pustym userType — czyli pełny, niefiltrowany
  // dostęp — zanim fetch poniżej zdąży wrócić. Inicjalizujemy więc stan z ostatniej
  // znanej wartości, a świeże dane i tak nadpisują ją zaraz po fetchu.
  const [userType, setUserType] = useState(() => localStorage.getItem('profileTypeFromLocalStorage') || '');
  // lista kluczy modułów (np. ['home','orders',...]) z profiles.permissions —
  // null/puste = pełen dostęp (domyślne, wsteczna kompatybilność dla kont bez
  // jawnie ustawionych uprawnień); ma znaczenie tylko dla roli 'manager' (Biuro),
  // Produkcja/Kierowca nie mają jeszcze modułów do granularnego ograniczania
  const [permissions, setPermissions] = useState(() => {
    try {
      const cached = localStorage.getItem('permissionsFromLocalStorage');
      return cached ? JSON.parse(cached) : null;
    } catch {
      return null;
    }
  });
  const [anchorElNav, setAnchorElNav] = useState(null);

  const layoutKey = `${storageKeyPrefix}NavLayout`;
  const visibilityKey = `${storageKeyPrefix}NavVisibility`;

  const [navLayout, setNavLayout] = useState(() => localStorage.getItem(layoutKey) || 'top');
  // 'expanded' | 'collapsed' (mini-rail / compact bar) | 'hidden' (fully collapsed, no reserved space)
  const [navVisibility, setNavVisibility] = useState(() => localStorage.getItem(visibilityKey) || 'expanded');

  const handleOpenNavMenu = (event) => setAnchorElNav(event.currentTarget);
  const handleCloseNavMenu = () => setAnchorElNav(null);

  useEffect(() => {
    const userId = localStorage.getItem('userIdFromLocalStorage');

    if (!userId) {
      navigate('/');
      return;
    }

    const fetchProfileData = async () => {
      const { data, error } = await supabase
        .from('profiles')
        .select('profile_type, username, permissions')
        .eq('id', userId)
        .single();

      if (error) {
        console.error('Error fetching profile:', error);
      } else if (data) {
        setUserType(data.profile_type);
        setPermissions(data.permissions);
        localStorage.setItem('profileTypeFromLocalStorage', data.profile_type || '');
        localStorage.setItem('permissionsFromLocalStorage', JSON.stringify(data.permissions || null));
      }
    };

    fetchProfileData();
  }, [navigate]);

  useEffect(() => {
    if (userType && !allowedProfileTypes.includes(userType)) {
      window.location.href = redirectTo;
    }
  }, [userType, allowedProfileTypes, redirectTo]);

  // dla roli 'manager' (Biuro) z jawnie ustawionymi uprawnieniami — ogranicza
  // widoczne pozycje menu do przyznanych modułów; Home jest zawsze widoczny
  // (to strona lądowania po zalogowaniu, nie można jej odebrać)
  const hasModulePermissions = userType === 'manager' && Array.isArray(permissions) && permissions.length > 0;
  const visibleNavItems = useMemo(() => {
    if (!hasModulePermissions) return navItems;
    return navItems.filter((item) => item.key === 'home' || permissions.includes(item.key));
  }, [navItems, hasModulePermissions, permissions]);

  // miękki redirect przy bezpośrednim wejściu w URL modułu, do którego ten
  // manager nie ma przyznanego dostępu (ta sama logika/ograniczenia co
  // powyższy redirect roli — klientowa, nie RLS)
  useEffect(() => {
    if (!hasModulePermissions) return;
    const current = location.pathname.toLowerCase();
    if (current === '/home') return;
    const allowed = visibleNavItems.some((item) => item.path.toLowerCase() === current);
    if (!allowed) {
      navigate('/home');
    }
  }, [hasModulePermissions, visibleNavItems, location.pathname, navigate]);

  const toggleLayout = () => {
    const newLayout = navLayout === 'top' ? 'side' : 'top';
    setNavLayout(newLayout);
    localStorage.setItem(layoutKey, newLayout);
  };

  const setVisibility = (next) => {
    setNavVisibility(next);
    localStorage.setItem(visibilityKey, next);
  };

  const toggleCollapsed = () => setVisibility(navVisibility === 'collapsed' ? 'expanded' : 'collapsed');
  const hideNav = () => setVisibility('hidden');
  const revealNav = () => setVisibility('collapsed');

  const SignOut = async () => {
    handleCloseNavMenu();
    localStorage.clear();
    const { error } = await supabase.auth.signOut();
    if (error) {
      console.error('Error with signout:', error);
    } else {
      navigate('/');
    }
  };

  const handleNavigate = (path) => {
    handleCloseNavMenu();
    navigate(path);
  };

  const isSide = navLayout === 'side';
  const collapsed = navVisibility === 'collapsed';
  const hidden = navVisibility === 'hidden';
  const sideWidth = collapsed ? RAIL_WIDTH : DRAWER_WIDTH;
  const barHeight = collapsed ? BAR_HEIGHT_SM : BAR_HEIGHT;
  const navBgColor = theme.palette.primary.main;
  const isActive = (path) => location.pathname.toLowerCase() === path.toLowerCase();

  if (hidden) {
    return (
      <Box sx={{ position: 'relative', minHeight: '100vh' }}>
        <Tooltip title="Pokaż menu" placement="right">
          <IconButton
            onClick={revealNav}
            sx={{
              position: 'fixed',
              top: 16,
              left: 16,
              zIndex: 1300,
              backgroundColor: navBgColor,
              color: '#fff',
              boxShadow: '0px 4px 12px rgba(0,0,0,0.25)',
              borderRadius: '12px',
              padding: '10px',
              '&:hover': { backgroundColor: navBgColor, filter: 'brightness(0.9)' },
            }}
          >
            <MenuIcon fontSize="medium" />
          </IconButton>
        </Tooltip>
        {children}
      </Box>
    );
  }

  return (
    <Box sx={{ display: 'flex', minHeight: '100vh' }}>
      {isSide ? (
        <Drawer
          variant="permanent"
          sx={{
            width: sideWidth,
            flexShrink: 0,
            whiteSpace: 'nowrap',
            transition: TRANSITION,
            [`& .MuiDrawer-paper`]: {
              width: sideWidth,
              overflowX: 'hidden',
              boxSizing: 'border-box',
              backgroundColor: navBgColor,
              color: '#ffffff',
              borderRight: 'none',
              boxShadow: '4px 0px 15px rgba(0, 0, 0, 0.15)',
              transition: TRANSITION,
            },
          }}
        >
          <IdConfigurationGuard />

          <Toolbar
            sx={{
              px: collapsed ? 1 : 3,
              py: 2,
              display: 'flex',
              alignItems: 'center',
              justifyContent: collapsed ? 'center' : 'space-between',
            }}
          >
            {!collapsed && (
              <Typography variant="h6" component="a" href="/home" sx={{ textDecoration: 'none', display: 'flex', alignItems: 'center' }}>
                <img src={`${process.env.PUBLIC_URL}/logo-herb.png`} alt="HERB" style={{ height: '36px' }} />
              </Typography>
            )}
            <Tooltip title={collapsed ? 'Rozwiń menu' : 'Zwiń menu'} placement="right">
              <IconButton onClick={toggleCollapsed} sx={{ color: 'rgba(255,255,255,0.8)', '&:hover': { color: '#fff' } }}>
                {collapsed ? <ChevronRightIcon /> : <ChevronLeftIcon />}
              </IconButton>
            </Tooltip>
          </Toolbar>

          <List sx={{ px: 1.5, py: 2, flexGrow: 1 }}>
            {visibleNavItems.map((item) => {
              const active = isActive(item.path);
              const buttonContent = (
                <ListItemButton
                  onClick={() => handleNavigate(item.path)}
                  sx={{
                    borderRadius: '8px',
                    justifyContent: collapsed ? 'center' : 'flex-start',
                    px: collapsed ? 1.5 : 2,
                    borderLeft: active ? '3px solid #fff' : '3px solid transparent',
                    backgroundColor: active ? 'rgba(255, 255, 255, 0.18)' : 'transparent',
                    '&:hover': { backgroundColor: 'rgba(255, 255, 255, 0.12)' },
                  }}
                >
                  <Box sx={{ display: 'flex', color: '#fff', mr: collapsed ? 0 : 2 }}>{item.icon}</Box>
                  {!collapsed && <ListItemText primary={item.label} primaryTypographyProps={{ fontWeight: 800, color: '#fff' }} />}
                </ListItemButton>
              );
              return (
                <ListItem key={item.key} disablePadding sx={{ mb: 0.5 }}>
                  {collapsed ? (
                    <Tooltip title={item.label} placement="right">
                      {buttonContent}
                    </Tooltip>
                  ) : (
                    buttonContent
                  )}
                </ListItem>
              );
            })}
          </List>

          <Divider sx={{ backgroundColor: 'rgba(255, 255, 255, 0.2)' }} />

          <List sx={{ px: 1.5, py: 2 }}>
            <ListItem disablePadding sx={{ mb: 0.5 }}>
              {collapsed ? (
                <Tooltip title="Mój Profil" placement="right">
                  <ListItemButton
                    component="a"
                    href={profileHref}
                    sx={{ borderRadius: '8px', justifyContent: 'center', px: 1.5, '&:hover': { backgroundColor: 'rgba(255, 255, 255, 0.12)' } }}
                  >
                    <AccountCircleIcon sx={{ color: 'rgba(255,255,255,0.9)' }} />
                  </ListItemButton>
                </Tooltip>
              ) : (
                <ListItemButton component="a" href={profileHref} sx={{ borderRadius: '8px', '&:hover': { backgroundColor: 'rgba(255, 255, 255, 0.12)' } }}>
                  <AccountCircleIcon sx={{ mr: 2, color: 'rgba(255,255,255,0.9)' }} />
                  <ListItemText primary="Mój Profil" primaryTypographyProps={{ fontWeight: 700, fontSize: '0.9rem', color: '#fff' }} />
                </ListItemButton>
              )}
            </ListItem>
            <ListItem disablePadding sx={{ mb: 0.5 }}>
              {collapsed ? (
                <Tooltip title="Przełącz na pasek górny" placement="right">
                  <ListItemButton onClick={toggleLayout} sx={{ borderRadius: '8px', justifyContent: 'center', px: 1.5, '&:hover': { backgroundColor: 'rgba(255, 255, 255, 0.12)' } }}>
                    <ViewStreamIcon sx={{ color: 'rgba(255,255,255,0.9)' }} />
                  </ListItemButton>
                </Tooltip>
              ) : (
                <ListItemButton onClick={toggleLayout} sx={{ borderRadius: '8px', '&:hover': { backgroundColor: 'rgba(255, 255, 255, 0.12)' } }}>
                  <ViewStreamIcon sx={{ mr: 2, color: 'rgba(255,255,255,0.9)' }} />
                  <ListItemText primary="Przełącz na pasek górny" primaryTypographyProps={{ fontWeight: 700, fontSize: '0.85rem', color: '#fff' }} />
                </ListItemButton>
              )}
            </ListItem>
            <ListItem disablePadding sx={{ mb: 0.5 }}>
              {collapsed ? (
                <Tooltip title="Schowaj pasek całkowicie" placement="right">
                  <ListItemButton onClick={hideNav} sx={{ borderRadius: '8px', justifyContent: 'center', px: 1.5, '&:hover': { backgroundColor: 'rgba(255, 255, 255, 0.12)' } }}>
                    <VisibilityOffIcon sx={{ color: 'rgba(255,255,255,0.9)' }} />
                  </ListItemButton>
                </Tooltip>
              ) : (
                <ListItemButton onClick={hideNav} sx={{ borderRadius: '8px', '&:hover': { backgroundColor: 'rgba(255, 255, 255, 0.12)' } }}>
                  <VisibilityOffIcon sx={{ mr: 2, color: 'rgba(255,255,255,0.9)' }} />
                  <ListItemText primary="Schowaj pasek całkowicie" primaryTypographyProps={{ fontWeight: 700, fontSize: '0.85rem', color: '#fff' }} />
                </ListItemButton>
              )}
            </ListItem>
            <ListItem disablePadding>
              {collapsed ? (
                <Tooltip title="Wyloguj" placement="right">
                  <ListItemButton onClick={SignOut} sx={{ borderRadius: '8px', justifyContent: 'center', px: 1.5, backgroundColor: 'rgba(0, 0, 0, 0.2)', '&:hover': { backgroundColor: 'rgba(0, 0, 0, 0.35)' } }}>
                    <LogoutIcon sx={{ color: '#ffb4b4' }} />
                  </ListItemButton>
                </Tooltip>
              ) : (
                <ListItemButton onClick={SignOut} sx={{ borderRadius: '8px', backgroundColor: 'rgba(0, 0, 0, 0.2)', '&:hover': { backgroundColor: 'rgba(0, 0, 0, 0.35)' } }}>
                  <LogoutIcon sx={{ mr: 2, color: '#ffb4b4' }} />
                  <ListItemText primary="Wyloguj" primaryTypographyProps={{ fontWeight: 800, color: '#ffb4b4' }} />
                </ListItemButton>
              )}
            </ListItem>
          </List>
        </Drawer>
      ) : (
        <AppBar position="fixed">
          <IdConfigurationGuard />
          <Container maxWidth="xl">
            <Toolbar disableGutters sx={{ minHeight: barHeight, transition: TRANSITION }}>
              <Typography
                variant="h6"
                noWrap
                component="a"
                href="/home"
                sx={{ mr: 2, display: { xs: 'none', md: 'flex' }, textDecoration: 'none' }}
              >
                <img
                  src={`${process.env.PUBLIC_URL}/logo-herb.png`}
                  alt="HERB"
                  style={{ height: collapsed ? '32px' : 'calc(54px * 1.05)', marginRight: 8, transition: 'height 0.25s ease' }}
                />
              </Typography>

              {/* MENU MOBILNE (HAMBURGER) */}
              <Box sx={{ flexGrow: 1, display: { xs: 'flex', md: 'none' } }}>
                <IconButton size="large" aria-label="menu" aria-controls="menu-appbar" aria-haspopup="true" onClick={handleOpenNavMenu} color="inherit">
                  <MenuIcon />
                </IconButton>
                <Menu
                  id="menu-appbar"
                  anchorEl={anchorElNav}
                  anchorOrigin={{ vertical: 'bottom', horizontal: 'left' }}
                  keepMounted
                  transformOrigin={{ vertical: 'top', horizontal: 'left' }}
                  open={Boolean(anchorElNav)}
                  onClose={handleCloseNavMenu}
                  sx={{
                    display: { xs: 'block', md: 'none' },
                    '& .MuiPaper-root': { minWidth: '250px', padding: '8px' },
                    '& .MuiButton-root': { fontSize: '18px', fontWeight: 800, color: '#0166FF', justifyContent: 'flex-start', width: '100%' },
                  }}
                >
                  {visibleNavItems.map((item) => (
                    <Button key={item.key} onClick={() => handleNavigate(item.path)} sx={{ my: 1 }}>
                      {item.label}
                    </Button>
                  ))}
                  <Button onClick={toggleLayout} sx={{ my: 1, fontSize: '0.9rem', color: '#555 !important' }}>Przełącz na bok</Button>
                  <Button onClick={toggleCollapsed} sx={{ my: 1, fontSize: '0.9rem', color: '#555 !important' }}>
                    {collapsed ? 'Rozwiń pasek' : 'Zwiń pasek'}
                  </Button>
                  <Button onClick={hideNav} sx={{ my: 1, fontSize: '0.9rem', color: '#555 !important' }}>Schowaj pasek całkowicie</Button>
                  <Button onClick={SignOut} sx={{ my: 1, fontSize: '1rem', color: '#d32f2f !important' }}>Wyloguj</Button>
                </Menu>
              </Box>

              {/* LOGO (MOBILE) */}
              <Typography
                variant="h5"
                noWrap
                component="a"
                href="/home"
                sx={{ mr: 2, display: { xs: 'flex', md: 'none' }, flexGrow: 1, textDecoration: 'none' }}
              >
                <img src={`${process.env.PUBLIC_URL}/logo-herb.png`} alt="HERB" style={{ height: 'calc(54px * 1.05)', marginRight: 8 }} />
              </Typography>

              {/* MENU NA DESKTOP */}
              <Box sx={{ flexGrow: 1, display: { xs: 'none', md: 'flex' }, alignItems: 'center' }}>
                {visibleNavItems.map((item) => {
                  const active = isActive(item.path);
                  return collapsed ? (
                    <Tooltip key={item.key} title={item.label} placement="bottom">
                      <IconButton
                        onClick={() => handleNavigate(item.path)}
                        sx={{
                          mx: 0.5,
                          color: 'white',
                          backgroundColor: active ? 'rgba(255,255,255,0.18)' : 'transparent',
                          '&:hover': { backgroundColor: 'rgba(255,255,255,0.12)' },
                        }}
                      >
                        {item.icon}
                      </IconButton>
                    </Tooltip>
                  ) : (
                    <Button
                      key={item.key}
                      onClick={() => handleNavigate(item.path)}
                      sx={{
                        my: 2,
                        color: 'white',
                        display: 'block',
                        borderBottom: active ? '2px solid #fff' : '2px solid transparent',
                        borderRadius: 0,
                      }}
                    >
                      {item.label}
                    </Button>
                  );
                })}
              </Box>

              {/* SEKCJA PROFILU I WYLOGOWANIA */}
              <Box sx={{ flexGrow: 0, display: 'flex', alignItems: 'center', gap: 1 }}>
                <Tooltip title="Przełącz na pasek boczny">
                  <IconButton color="inherit" onClick={toggleLayout}>
                    <ViewSidebarIcon />
                  </IconButton>
                </Tooltip>
                <Tooltip title={collapsed ? 'Rozwiń pasek' : 'Zwiń pasek'}>
                  <IconButton color="inherit" onClick={toggleCollapsed}>
                    {collapsed ? <ChevronRightIcon /> : <ChevronLeftIcon />}
                  </IconButton>
                </Tooltip>
                <Tooltip title="Schowaj pasek całkowicie">
                  <IconButton color="inherit" onClick={hideNav}>
                    <VisibilityOffIcon />
                  </IconButton>
                </Tooltip>

                <Typography
                  noWrap
                  component="a"
                  href={profileHref}
                  sx={{ ml: 1, mr: 2, color: 'inherit', textDecoration: 'none', display: 'flex', alignItems: 'center' }}
                >
                  <AccountCircleIcon fontSize="large" />
                </Typography>
                <Button variant="contained" color="error" onClick={SignOut} sx={{ fontSize: 'calc(0.875rem * 1.05)', fontWeight: 800 }}>
                  Wyloguj
                </Button>
              </Box>
            </Toolbar>
          </Container>
        </AppBar>
      )}

      <Box component="main" sx={{ flexGrow: 1, minWidth: 0, display: 'flex', flexDirection: 'column' }}>
        {!isSide && <Toolbar sx={{ minHeight: barHeight, transition: TRANSITION }} />}
        {children}
      </Box>
    </Box>
  );
}

export default NavShell;
